import {
  FormatAlignCenter as AlignCenterIcon,
  FormatAlignLeft as AlignLeftIcon,
  FormatAlignRight as AlignRightIcon,
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  TextFields as UppercaseIcon,
} from '@mui/icons-material';
import { Stack, ToggleButtonGroup, Typography } from '@mui/material';
import React, { ReactNode, useEffect, useState } from 'react';
import { ContactCaptureTextAlign } from '../../types';
import { TextStyleSizeField, TextStyleToggleButton } from './ContactCaptureTextStyleControls.styles';

const ALIGN_OPTIONS: Array<{ value: ContactCaptureTextAlign; label: string; icon: ReactNode }> = [
  { value: 'left', label: 'Align left', icon: <AlignLeftIcon fontSize="small" /> },
  { value: 'center', label: 'Align center', icon: <AlignCenterIcon fontSize="small" /> },
  { value: 'right', label: 'Align right', icon: <AlignRightIcon fontSize="small" /> },
];

export interface ContactCaptureTextStyleControlsProps {
  label: string;
  disabled: boolean;
  align: ContactCaptureTextAlign;
  size: number;
  sizeRange: { min: number; max: number };
  bold: boolean;
  italic: boolean;
  uppercase: boolean;
  onAlignChange: (value: ContactCaptureTextAlign) => void;
  onSizeChange: (value: number) => void;
  onBoldChange: (value: boolean) => void;
  onItalicChange: (value: boolean) => void;
  onUppercaseChange: (value: boolean) => void;
}

export const ContactCaptureTextStyleControls = ({
  label,
  disabled,
  align,
  size,
  sizeRange,
  bold,
  italic,
  uppercase,
  onAlignChange,
  onSizeChange,
  onBoldChange,
  onItalicChange,
  onUppercaseChange,
}: ContactCaptureTextStyleControlsProps) => {
  // A draft lets the field be cleared while typing; the clamped value is committed on blur.
  const [sizeDraft, setSizeDraft] = useState(String(size));
  useEffect(() => setSizeDraft(String(size)), [size]);

  const commitSize = () => {
    const value = Number(sizeDraft);
    const next = sizeDraft.trim() && Number.isFinite(value)
      ? Math.round(Math.max(sizeRange.min, Math.min(sizeRange.max, value)))
      : size;
    setSizeDraft(String(next));
    if (next !== size) onSizeChange(next);
  };

  const formatOptions = [
    { value: 'bold', label: 'Bold', icon: <BoldIcon fontSize="small" />, checked: bold, onChange: onBoldChange },
    { value: 'italic', label: 'Italic', icon: <ItalicIcon fontSize="small" />, checked: italic, onChange: onItalicChange },
    {
      value: 'uppercase',
      label: 'Uppercase',
      icon: <UppercaseIcon fontSize="small" />,
      checked: uppercase,
      onChange: onUppercaseChange,
    },
  ];
  const formats = formatOptions.filter(option => option.checked).map(option => option.value);

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" fontWeight="fontWeightSemiBold">{label}</Typography>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
        <ToggleButtonGroup
          size="small"
          exclusive
          disabled={disabled}
          value={align}
          aria-label={`${label} alignment`}
          onChange={(_, value: ContactCaptureTextAlign | null) => value && onAlignChange(value)}
        >
          {ALIGN_OPTIONS.map(option => (
            <TextStyleToggleButton key={option.value} value={option.value} aria-label={option.label}>
              {option.icon}
            </TextStyleToggleButton>
          ))}
        </ToggleButtonGroup>
        <ToggleButtonGroup
          size="small"
          disabled={disabled}
          value={formats}
          aria-label={`${label} formatting`}
          onChange={(_, value: string[]) => formatOptions.forEach(option => {
            if (value.includes(option.value) !== option.checked) option.onChange(!option.checked);
          })}
        >
          {formatOptions.map(option => (
            <TextStyleToggleButton key={option.value} value={option.value} aria-label={option.label}>
              {option.icon}
            </TextStyleToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextStyleSizeField
          disabled={disabled}
          type="number"
          size="small"
          label="Size"
          value={sizeDraft}
          onChange={event => setSizeDraft(event.target.value)}
          onBlur={commitSize}
          onKeyDown={event => event.key === 'Enter' && commitSize()}
          inputProps={{ min: sizeRange.min, max: sizeRange.max, 'aria-label': `${label} size` }}
          helperText={`${sizeRange.min}–${sizeRange.max} px`}
        />
      </Stack>
    </Stack>
  );
};
